import React, { useState, useEffect } from 'react';
import { Play, FileText, Sparkles, HelpCircle, CheckCircle2, XCircle, BookOpen, AlertCircle, Lightbulb } from 'lucide-react';
import { ProtectedVideo, InlinePdf } from './ProtectedMedia';

export const LessonContent = ({ lesson }) => {
  const [activeTab, setActiveTab] = useState('theory'); // 'theory' | 'points' | 'quiz' | 'pdf'
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [revealedAnswers, setRevealedAnswers] = useState({});

  useEffect(() => {
    setActiveTab('theory');
    setSelectedAnswers({});
    setRevealedAnswers({});
  }, [lesson?.id]);

  if (!lesson) return null;

  const quizQuestions = Array.isArray(lesson.quiz) ? lesson.quiz : [];
  const theory = lesson.theory || null;

  const handleSelectOption = (qIdx, optIdx) => {
    setSelectedAnswers(prev => ({ ...prev, [qIdx]: optIdx }));
  };

  const handleCheckAnswer = (qIdx) => {
    setRevealedAnswers(prev => ({ ...prev, [qIdx]: true }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Video de la Lección */}
      <div style={{
        background: '#0a1a2a',
        border: '1px solid rgba(197, 160, 89, 0.3)',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
        position: 'relative',
        aspectRatio: '16/9'
      }}>
        {lesson.video_url ? (
          <ProtectedVideo
            src={lesson.video_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: '#0f2035', gap: '12px'
          }}>
            <Play className="w-12 h-12" style={{ color: 'var(--text-muted)' }} />
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Clase teórica en video para esta lección.</p>
          </div>
        )}
      </div>

      {/* Título y Módulo */}
      <div>
        <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
          {lesson.module}
        </span>
        <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
          {lesson.title}
        </h1>
      </div>

      {/* Pestañas de Contenido */}
      <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(197, 160, 89, 0.2)', background: 'rgba(10, 26, 42, 0.6)', overflowX: 'auto' }}>
          {[
            { id: 'theory', label: '📖 Clase Teórica', icon: BookOpen },
            { id: 'points', label: '📌 Puntos Clave', icon: Sparkles },
            { id: 'quiz', label: `📝 Quiz Ceneval (${quizQuestions.length})`, icon: HelpCircle },
            { id: 'pdf', label: `📄 Material PDF (${lesson.pdf_resources?.length || 0})`, icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            const isTabActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`btn-vx-cat${isTabActive ? ' active' : ''}`}
                style={{
                  borderRadius: 0,
                  border: 'none',
                  borderBottom: isTabActive ? '2px solid var(--gold-matte)' : 'none',
                  padding: '12px 20px',
                  whiteSpace: 'nowrap',
                  background: isTabActive ? 'rgba(197,160,89,0.1)' : 'transparent',
                  color: isTabActive ? 'var(--gold-matte)' : 'var(--text-secondary)',
                  cursor: 'pointer'
                }}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{ padding: '1.5rem' }}>

          {/* 1. CLASE TEÓRICA EXHAUSTIVA */}
          {activeTab === 'theory' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem', lineHeight: 1.7 }}>
              
              {/* Introducción y Enfoque Ceneval */}
              {theory?.intro ? (
                <div style={{
                  padding: '16px 20px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.08)',
                  border: '1px solid rgba(99, 102, 241, 0.25)', display: 'flex', gap: '14px', alignItems: 'flex-start'
                }}>
                  <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', flexShrink: 0 }}>
                    <Lightbulb size={20} />
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '0.85rem', color: '#a5b4fc', fontWeight: 'bold' }}>
                      Enfoque del EXANI-II 2026
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.88rem', color: '#cbd5e1' }}>
                      {theory.intro}
                    </p>
                  </div>
                </div>
              ) : null}

              {/* Secciones de Contenido Teórico */}
              {theory?.sections?.map((sec, sIdx) => (
                <div key={sIdx} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <h3 style={{
                    margin: 0, fontSize: '1.1rem', color: '#fff', fontWeight: '700',
                    borderLeft: '4px solid var(--gold-matte)', paddingLeft: '12px'
                  }}>
                    {sec.subtitle}
                  </h3>
                  <div style={{
                    fontSize: '0.9rem', color: '#e2e8f0', whiteSpace: 'pre-line',
                    background: 'rgba(10, 26, 42, 0.4)', padding: '16px', borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    {sec.content}
                  </div>
                </div>
              ))}

              {/* Ejemplos Demostrativos Resueltos Paso a Paso */}
              {theory?.examples?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#34d399', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle2 size={18} />
                    <span>Ejemplo Modelo Resuelto Paso a Paso (Tipo Ceneval)</span>
                  </h3>
                  {theory.examples.map((ex, eIdx) => (
                    <div key={eIdx} style={{
                      padding: '16px 20px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.06)',
                      border: '1px solid rgba(16, 185, 129, 0.25)', display: 'flex', flexDirection: 'column', gap: '10px'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '0.92rem' }}>
                        📌 Planteamiento: {ex.problem}
                      </div>
                      <div style={{
                        fontSize: '0.86rem', color: '#cbd5e1', whiteSpace: 'pre-line',
                        background: 'rgba(0, 0, 0, 0.25)', padding: '12px 14px', borderRadius: '8px'
                      }}>
                        {ex.solution}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Trampas Comunes y Tips de Examen */}
              {theory?.exam_tips && (
                <div style={{
                  padding: '16px 20px', borderRadius: '12px', background: 'rgba(197, 160, 89, 0.08)',
                  border: '1px solid rgba(197, 160, 89, 0.3)', display: 'flex', gap: '14px', alignItems: 'flex-start'
                }}>
                  <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(197, 160, 89, 0.15)', color: 'var(--gold-matte)', flexShrink: 0 }}>
                    <AlertCircle size={20} />
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '0.85rem', color: 'var(--gold-matte)', fontWeight: 'bold' }}>
                      ⚠️ Trampas Comunes y Recomendación Docente
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.86rem', color: '#e2e8f0' }}>
                      {theory.exam_tips}
                    </p>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* 2. PUNTOS CLAVE */}
          {activeTab === 'points' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 className="vx-modal-sec-title">📌 Conceptos Esenciales Evaluados en el EXANI-II</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {lesson.key_points && lesson.key_points.length > 0 ? (
                  lesson.key_points.map((point, index) => (
                    <div key={index} style={{
                      padding: '12px 16px', borderRadius: '8px', background: 'rgba(10, 26, 42, 0.6)',
                      border: '1px solid rgba(197, 160, 89, 0.15)', fontSize: '0.85rem', color: 'var(--text-secondary)',
                      display: 'flex', alignItems: 'flex-start', gap: '12px', lineHeight: 1.6
                    }}>
                      <span style={{
                        width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(197, 160, 89, 0.2)',
                        color: 'var(--gold-matte)', fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                      }}>
                        {index + 1}
                      </span>
                      <span>{point}</span>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No hay puntos clave destacados.</p>
                )}
              </div>
            </div>
          )}

          {/* 3. QUIZ CENEVAL (3 OPCIONES) */}
          {activeTab === 'quiz' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="vx-modal-sec-title">📝 Autoevaluación Tipo Ceneval (3 Opciones de Respuesta)</h3>
                <span style={{ fontSize: '0.72rem', color: '#c5a059', fontFamily: 'monospace' }}>
                  {quizQuestions.length} reactivo(s)
                </span>
              </div>

              {quizQuestions.length > 0 ? (
                quizQuestions.map((q, qIdx) => {
                  const userChoice = selectedAnswers[qIdx];
                  const isRevealed = revealedAnswers[qIdx];
                  const isCorrect = userChoice === q.correct_index;

                  return (
                    <div key={qIdx} style={{
                      padding: '16px', borderRadius: '12px', background: 'rgba(10, 26, 42, 0.7)',
                      border: isRevealed ? (isCorrect ? '1px solid rgba(52, 211, 153, 0.5)' : '1px solid rgba(239, 68, 68, 0.5)') : '1px solid rgba(197, 160, 89, 0.2)',
                      display: 'flex', flexDirection: 'column', gap: '12px'
                    }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '6px', background: 'rgba(197, 160, 89, 0.2)',
                          color: 'var(--gold-matte)', fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace', flexShrink: 0
                        }}>
                          R{qIdx + 1}
                        </span>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#fff', fontWeight: 600, lineHeight: 1.5 }}>
                          {q.question}
                        </p>
                      </div>

                      {/* Opciones */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        {(q.options || []).map((opt, oIdx) => {
                          const letters = ['A', 'B', 'C'];
                          const isSelected = userChoice === oIdx;
                          let bg = 'rgba(255, 255, 255, 0.04)';
                          let border = '1px solid rgba(255, 255, 255, 0.1)';
                          let color = '#cbd5e1';

                          if (isSelected) {
                            bg = 'rgba(197, 160, 89, 0.15)';
                            border = '1px solid var(--gold-matte)';
                            color = '#fff';
                          }

                          if (isRevealed) {
                            if (oIdx === q.correct_index) {
                              bg = 'rgba(16, 185, 129, 0.2)';
                              border = '1px solid #10b981';
                              color = '#6ee7b7';
                            } else if (isSelected && !isCorrect) {
                              bg = 'rgba(239, 68, 68, 0.2)';
                              border = '1px solid #ef4444';
                              color = '#fca5a5';
                            }
                          }

                          return (
                            <button
                              key={oIdx}
                              disabled={isRevealed}
                              onClick={() => handleSelectOption(qIdx, oIdx)}
                              style={{
                                padding: '10px 14px', borderRadius: '8px', background: bg, border,
                                color, textAlign: 'left', cursor: isRevealed ? 'default' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <span style={{
                                width: '22px', height: '22px', borderRadius: '50%', background: isSelected ? 'var(--gold-matte)' : 'rgba(255, 255, 255, 0.1)',
                                color: isSelected ? '#000' : '#fff', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                              }}>
                                {letters[oIdx] || oIdx + 1}
                              </span>
                              <span>{opt}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Botón de Comprobar */}
                      {!isRevealed && userChoice !== undefined && (
                        <button
                          onClick={() => handleCheckAnswer(qIdx)}
                          className="btn-vx-action"
                          style={{ alignSelf: 'flex-start', padding: '6px 16px', fontSize: '0.8rem', marginTop: '6px' }}
                        >
                          Verificar Respuesta
                        </button>
                      )}

                      {/* Explicación si ya se comprobó */}
                      {isRevealed && (
                        <div style={{
                          padding: '10px 14px', borderRadius: '8px',
                          background: isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          border: isCorrect ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                          display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', color: isCorrect ? '#34d399' : '#f87171' }}>
                            {isCorrect ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                            <span>{isCorrect ? '¡Correcto!' : 'Respuesta Incorrecta'}</span>
                          </div>
                          {q.explanation && (
                            <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                              <strong>Fundamento:</strong> {q.explanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No hay reactivos de autoevaluación cargados para esta lección.
                </p>
              )}
            </div>
          )}

          {/* 4. PDF */}
          {activeTab === 'pdf' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 className="vx-modal-sec-title">📄 Material y Formularios en PDF</h3>
              {lesson.pdf_resources && lesson.pdf_resources.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {lesson.pdf_resources.map((pdf, pIdx) => (
                    <InlinePdf key={pdf.id || pIdx} url={pdf.url} name={pdf.name || `Documento ${pIdx + 1}`} />
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No hay archivos PDF adjuntos en esta lección.
                </p>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
